import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Wallet, 
  Users, 
  ShoppingCart, 
  TrendingUp, 
  Copy, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  Clock, 
  Lock, 
  AlertTriangle,
  Shield,
  Plus,
  Building2,
  X,
  FileText,
  Tag,
  UserCheck,
  CreditCard,
  ArrowRightLeft,
  Palette,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import SupportAccess from '@/components/SupportAccess';
import SynergyPartnerAccess from '@/components/SynergyPartnerAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Welcome Card ─────────────────────────────────────────────────── */
const SYNERGY_FEATURES = [
  { icon: FileText,       label: 'Quotations',    desc: 'Create and send product quotations to your clients' },
  { icon: ShoppingCart,   label: 'Orders',         desc: 'Place and track service orders' },
  { icon: Tag,            label: 'Pricing',        desc: 'View and manage your product pricing' },
  { icon: Users,          label: 'Clients',        desc: 'Add and manage your client accounts' },
  { icon: CreditCard,     label: 'Wallet',         desc: 'Top up your wallet and manage payments' },
  { icon: ArrowRightLeft, label: 'Transactions',   desc: 'View your full transaction history' },
  { icon: Palette,        label: 'White Label',    desc: 'Customise the portal branding for your clients' },
];

const CP_FEATURES = [
  { icon: FileText,     label: 'Quotations', desc: 'Create and send product quotations to your clients' },
  { icon: ShoppingCart, label: 'Orders',      desc: 'Place and track service orders' },
  { icon: Tag,          label: 'Pricing',     desc: 'View and manage your product pricing' },
  { icon: Users,        label: 'Clients',     desc: 'Add and manage your client accounts' },
];

function WelcomeCard({ role, onDismiss }) {
  const isCP = role === 'sub_reseller';
  const features = isCP ? CP_FEATURES : SYNERGY_FEATURES;
  const roleLabel = isCP ? 'Channel Partner' : 'Synergy Partner';

  return (
    <div
      data-testid="welcome-card"
      className="relative bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl p-6 text-white shadow-xl overflow-hidden mb-6"
    >
      {/* background decoration */}
      <div className="absolute -right-10 -top-10 w-48 h-48 bg-white/10 rounded-full" />
      <div className="absolute -right-4 -bottom-8 w-32 h-32 bg-white/10 rounded-full" />

      {/* dismiss */}
      <button
        data-testid="welcome-card-dismiss"
        onClick={onDismiss}
        className="absolute top-4 right-4 p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors z-10"
        aria-label="Dismiss welcome card"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="h-5 w-5 text-white/90" />
          <span className="text-sm font-medium text-white/80 uppercase tracking-wide">KYC Approved</span>
        </div>
        <h2 className="text-2xl font-bold mb-1">Welcome to CloudSetu!</h2>
        <p className="text-white/80 text-sm mb-5">
          Your <strong>{roleLabel}</strong> account is now active. Here's a quick overview of what you can do:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {features.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-start gap-3 bg-white/15 rounded-xl px-4 py-3">
              <div className="p-1.5 rounded-lg bg-white/20 shrink-0 mt-0.5">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">{label}</p>
                <p className="text-xs text-white/70 mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <Button
          data-testid="welcome-card-got-it"
          onClick={onDismiss}
          className="mt-5 bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 h-10 rounded-xl shadow-md"
        >
          Got it, let's go!
        </Button>
      </div>
    </div>
  );
}

function SupportPinInline() {
  const [pin, setPin] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    fetchCurrentPin();
  }, []);

  useEffect(() => {
    if (!expiresAt) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;
      
      if (diff <= 0) {
        setPin(null);
        setExpiresAt(null);
        setTimeLeft('');
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  const fetchCurrentPin = async () => {
    try {
      const response = await axios.get(`${API}/support-pin/current`);
      if (response.data.pin) {
        setPin(response.data.pin);
        setExpiresAt(response.data.expires_at);
      }
    } catch (error) {
      // No active PIN
    }
  };

  const generatePin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/support-pin/generate`);
      setPin(response.data.pin);
      setExpiresAt(response.data.expires_at);
      setShowPin(true);
      toast.success('Support PIN generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PIN');
    } finally {
      setLoading(false);
    }
  };

  const copyPin = () => {
    if (pin) {
      navigator.clipboard.writeText(pin);
      toast.success('PIN copied to clipboard');
    }
  };

  const invalidatePin = async () => {
    try {
      await axios.delete(`${API}/support-pin/invalidate`);
      setPin(null);
      setExpiresAt(null);
      toast.success('PIN invalidated');
    } catch (error) {
      toast.error('Failed to invalidate PIN');
    }
  };

  if (!pin) {
    return (
      <div className="text-center py-4 sm:py-6">
        <p className="text-slate-500 text-sm sm:text-base mb-3 sm:mb-4 break-words whitespace-normal px-2">
          Generate a secure PIN to allow support access to your dashboard
        </p>
        <Button 
          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          onClick={generatePin}
          disabled={loading}
          aria-label="Generate Support PIN"
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
              Generating...
            </>
          ) : (
            'Generate Support PIN'
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* PIN Display */}
      <div className="bg-slate-50 rounded-lg border-2 border-slate-200 p-3 sm:p-4">
        <div className="text-xs text-slate-500 text-center mb-2">Your Support PIN</div>
        <div className="flex items-center justify-center gap-2 sm:gap-3">
          <div 
            className="text-2xl sm:text-3xl font-bold tracking-[0.3em] sm:tracking-[0.5em] text-blue-600"
            aria-label={showPin ? `PIN: ${pin}` : 'PIN hidden'}
          >
            {showPin ? pin : '••••'}
          </div>
          <button 
            onClick={() => setShowPin(!showPin)} 
            className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors"
            aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
          >
            {showPin ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>
        </div>
        {timeLeft && (
          <div className="flex items-center justify-center gap-1 mt-2 text-amber-600 text-xs sm:text-sm">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
            <span>Expires in {timeLeft}</span>
          </div>
        )}
      </div>

      {/* Action Buttons - Responsive flex wrap */}
      <div className="flex flex-wrap gap-2">
        <Button 
          size="sm" 
          variant="outline" 
          className="flex-1 min-w-[100px] text-xs sm:text-sm" 
          onClick={copyPin}
          aria-label="Copy PIN to clipboard"
        >
          <Copy className="w-3 h-3 mr-1" aria-hidden="true" />
          Copy PIN
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="flex-1 min-w-[100px] text-xs sm:text-sm" 
          onClick={generatePin} 
          disabled={loading}
          aria-label="Generate new PIN"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          New PIN
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={invalidatePin}
          className="px-3"
          aria-label="Invalidate PIN"
        >
          <Lock className="w-3 h-3" aria-hidden="true" />
        </Button>
      </div>

      {/* Warning - Full text visibility with word wrap */}
      <div 
        className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3 text-xs sm:text-sm text-amber-700"
        role="alert"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span className="break-words whitespace-normal leading-relaxed">
            Only share this PIN with authorized support personnel. The PIN will be invalidated after one use.
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ResellerDashboard() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selfServices, setSelfServices] = useState([]);
  const navigate = useNavigate();

  const isSynergyPartner = user?.role === 'reseller';
  const isChannelPartner = user?.role === 'sub_reseller';
  const [showWelcome, setShowWelcome] = useState(false);
  const [cpPendingCount, setCpPendingCount] = useState(0);

  useEffect(() => {
    fetchData();
    fetchWelcomeStatus();
  }, []);

  const fetchWelcomeStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/partner/welcome-status`);
      if (!data.has_seen_welcome) setShowWelcome(true);
    } catch {
      // silently ignore — welcome card is non-critical
    }
  };

  const handleDismissWelcome = async () => {
    setShowWelcome(false);
    try {
      await axios.post(`${API}/partner/welcome-dismiss`);
    } catch {
      // silently ignore
    }
  };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/clients`),
        axios.get(`${API}/orders`)
      ];
      // Only Synergy Partners fetch wallet (Channel Partners have no wallet)
      if (isSynergyPartner) {
        requests.push(axios.get(`${API}/wallet/balance`).catch(() => ({ data: null })));
        requests.push(axios.get(`${API}/sub-resellers`).catch(() => ({ data: [] })));
      }
      const responses = await Promise.all(requests);
      setClients(responses[0].data);
      setOrders(responses[1].data);
      if (isSynergyPartner) {
        setWallet(responses[2]?.data || null);
        setPartners(responses[3]?.data || []);
      }
      // Fetch self-services (partner's own orders)
      try {
        const svRes = await axios.get(`${API}/partner/self-orders`);
        setSelfServices((svRes.data || []).filter(o => !['cancelled'].includes(o.status)));
      } catch { /* silent */ }

      // Fetch CP pending approvals count (Synergy Partners only)
      if (user?.role === 'reseller') {
        try {
          const [cpQuotRes, cpSoRes] = await Promise.all([
            axios.get(`${API}/quotations/cp-pending`),
            axios.get(`${API}/partner/cp-self-orders/pending`)
          ]);
          setCpPendingCount(((cpQuotRes.data || []).length) + ((cpSoRes.data || []).length));
        } catch { /* silent */ }
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (path) => {
    if (path) navigate(path);
  };

  // Stats cards — Revenue card removed for all, Wallet hidden for Channel Partners
  const stats = [
    // Wallet only for Synergy Partners
    ...(isSynergyPartner ? [{
      title: 'Wallet Balance',
      value: wallet ? `₹${wallet.balance.toLocaleString()}` : '₹0',
      icon: Wallet,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      path: '/reseller/wallet'
    }] : []),
    {
      title: 'Total Clients',
      value: clients.length,
      icon: Users,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      path: '/reseller/clients'
    },
    {
      title: 'Total Orders',
      value: orders.length,
      icon: ShoppingCart,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-500',
      path: '/reseller/quotations'
    },
    // Pending CP Approvals — Synergy Partners only
    ...(isSynergyPartner ? [{
      title: 'Pending Approvals',
      value: cpPendingCount,
      icon: Clock,
      iconBg: cpPendingCount > 0 ? 'bg-amber-50' : 'bg-slate-50',
      iconColor: cpPendingCount > 0 ? 'text-amber-500' : 'text-slate-400',
      path: '/reseller/quotations'
    }] : [])
    // Revenue card removed for all roles per requirement
  ];

  const roleLabel = user?.role === 'sub_reseller' ? 'Channel Partner' : 'Synergy Partner';

  return (
    <DashboardLayout role={user?.role}>
      <div className="">
        {/* Welcome Card — shown only on first login after KYC approval */}
        {showWelcome && (
          <WelcomeCard role={user?.role} onDismiss={handleDismissWelcome} />
        )}

        {/* Header - Responsive */}
       <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between mb-4">
          <div className="min-w-0">
            <h1 
              className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight truncate" 
              data-testid="reseller-dashboard-title"
            >
              {roleLabel} Dashboard
            </h1>
            <p className="text-slate-500 text-sm sm:text-base">Manage your clients and services</p>
          </div>
          <div className="flex-shrink-0 flex flex-wrap gap-2">
            {isSynergyPartner ? (
              /* Synergy Partner: Two separate access buttons */
              <SynergyPartnerAccess />
            ) : (
              /* Channel Partner: Single Client Dashboard Access button */
              <SupportAccess />
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 sm:h-64" role="status" aria-label="Loading dashboard">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Stats Cards - Responsive Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-5">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card 
                    key={stat.title} 
                    className="bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                    data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleCardClick(stat.path)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && handleCardClick(stat.path)}
                    aria-label={`${stat.title}: ${stat.value}. Click to view details`}
                  >
                    <CardContent className="p-3 sm:p-4 lg:p-5">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 sm:space-y-2 lg:space-y-3 min-w-0 flex-1">
                          <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{stat.title}</p>
                          <p 
                            className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 truncate" 
                            data-testid={`stat-value-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {stat.value}
                          </p>
                          <p className="text-[10px] sm:text-xs text-slate-400 group-hover:text-blue-500 transition-colors hidden sm:block">
                            Click to view details
                          </p>
                        </div>
                        <div className={`${stat.iconBg} p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0 ml-2`}>
                          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${stat.iconColor}`} aria-hidden="true" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Three Cards Row: Support PIN/Recent Partners, Recent Clients, Recent Orders */}
            {/* Mobile: Stack vertically, Tablet: 2-col wrap, Desktop: 3-col grid */}
            <div className="grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
              
              {/* Conditional Card: Support PIN for Channel Partner, Recent Partners for Synergy Partner */}
              {isChannelPartner ? (
                /* Support PIN Card - Only for Channel Partner */
                <Card 
                  className="border-l-4 border-l-blue-500 bg-white shadow-sm h-auto" 
                  data-testid="support-pin-card"
                >
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <div className="bg-blue-50 p-2 rounded-lg">
                        <Shield className="h-5 w-5 text-blue-500" aria-hidden="true" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800">Support PIN</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4 break-words whitespace-normal">
                      This PIN grants secure, temporary access to your dashboard for support personnel.
                    </p>
                    <SupportPinInline />
                  </CardContent>
                </Card>
              ) : (
                /* Recent Partners Card - Only for Synergy Partner (same style as Recent Clients) */
                <Card 
                  className="bg-white shadow-sm h-auto" 
                  data-testid="recent-partners-card"
                >
                  <CardContent className="p-4 sm:p-5 lg:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 text-center mb-4">Recent Partners</h3>
                    <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                      <div className="mb-4 bg-green-50 p-4 rounded-full">
                        <Building2 className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-green-400" aria-hidden="true" />
                      </div>
                      {partners.length === 0 ? (
                        <>
                          <p className="text-slate-500 mb-4 text-sm sm:text-base text-center">No partners yet</p>
                          <Button 
                            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                            onClick={() => navigate('/reseller/clients')}
                            aria-label="Add your first partner"
                          >
                            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                            Add your first partner
                          </Button>
                        </>
                      ) : (
                        <>
                          <p className="text-slate-600 mb-4 text-sm sm:text-base">
                            <span className="text-2xl sm:text-3xl font-bold text-green-600">{partners.length}</span>
                            <span className="ml-2">partner{partners.length !== 1 ? 's' : ''}</span>
                          </p>
                          <Button 
                            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                            onClick={() => navigate('/reseller/clients')}
                            aria-label="View all partners"
                          >
                            View all partners
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Clients Card */}
              <Card 
                className="bg-white shadow-sm h-auto" 
                data-testid="recent-clients-card"
              >
                <CardContent className="p-4 sm:p-5 lg:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-800 text-center mb-4">Recent Clients</h3>
                  <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                    <div className="mb-4 bg-blue-50 p-4 rounded-full">
                      <Users className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-blue-400" aria-hidden="true" />
                    </div>
                    {clients.length === 0 ? (
                      <>
                        <p className="text-slate-500 mb-4 text-sm sm:text-base text-center">No clients yet</p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                          onClick={() => navigate('/reseller/clients')}
                          aria-label="Add your first client"
                        >
                          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                          Add your first client
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-slate-600 mb-4 text-sm sm:text-base">
                          <span className="text-2xl sm:text-3xl font-bold text-blue-600">{clients.length}</span>
                          <span className="ml-2">client{clients.length !== 1 ? 's' : ''}</span>
                        </p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                          onClick={() => navigate('/reseller/clients')}
                          aria-label="View all clients"
                        >
                          View all clients
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Orders Card */}
              <Card 
                className="bg-white shadow-sm h-auto" 
                data-testid="recent-orders-card"
              >
                <CardContent className="p-4 sm:p-5 lg:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-800 text-center mb-4">Recent Orders</h3>
                  <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                    <div className="mb-4 bg-purple-50 p-4 rounded-full">
                      <ShoppingCart className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-purple-400" aria-hidden="true" />
                    </div>
                    {orders.length === 0 ? (
                      <>
                        <p className="text-slate-500 mb-4 text-sm sm:text-base text-center">No orders yet</p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                          onClick={() => navigate('/reseller/quotations')}
                          aria-label="Create a quotation"
                        >
                          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                          Create a quotation
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-slate-600 mb-4 text-sm sm:text-base">
                          <span className="text-2xl sm:text-3xl font-bold text-purple-600">{orders.length}</span>
                          <span className="ml-2">order{orders.length !== 1 ? 's' : ''}</span>
                        </p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                          onClick={() => navigate('/reseller/orders')}
                          aria-label="View all orders"
                        >
                          View all orders
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Services Section (own self-ordered services) */}
            {selfServices.length > 0 && (
              <div className="mt-6" data-testid="all-services-section">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-slate-800">My Services</h2>
                  <Button variant="outline" size="sm" onClick={() => navigate('/reseller/my-services')} className="text-sm">
                    View All
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {selfServices.slice(0, 6).map(svc => {
                    const statusColors = {
                      active:          'bg-green-100  text-green-700',
                      pending:         'bg-amber-100  text-amber-700',
                      pending_payment: 'bg-orange-100 text-orange-800',
                      expired:         'bg-slate-100  text-slate-500',
                    };
                    return (
                      <Card key={svc.id} className="bg-white border border-slate-100 shadow-sm hover:shadow transition-shadow cursor-pointer"
                        onClick={() => navigate('/reseller/my-services')}
                        data-testid={`service-card-${svc.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-semibold text-slate-800 text-sm leading-tight">{svc.product_name}</p>
                            <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[svc.status] || 'bg-slate-100 text-slate-500'}`}>
                              {svc.status === 'pending_payment' ? 'Pending Payment' : svc.status?.charAt(0).toUpperCase() + svc.status?.slice(1)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">₹{(svc.total_amount || 0).toLocaleString('en-IN')}</p>
                          {svc.renewal_date && (
                            <p className="text-xs text-slate-400 mt-1">
                              Renewal: {new Date(svc.renewal_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          {svc.provisioning && (
                            <p className="text-xs text-green-600 mt-1 font-medium">✓ Credentials available</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
